import type { ChatAttachment, ReasoningPreference } from "@/api/chat";
import type { QuoteChatContext } from "@/api/quoteChatTransport";
import { quoteUuid } from "@/api/quoteChatArtifacts";
import type { JourneyPage } from "./quoteJourneyState";
export type AgentJourneyView = {
  reasoningPreference?: ReasoningPreference;
  initialReasoningPreference?: ReasoningPreference;
  sessionId: string;
  raw: string;
  attachments: ChatAttachment[];
  context: QuoteChatContext | null;
  originalQuery: string;
  initialPromptConsumed: boolean;
  initialPromptState: "pending" | "submitted" | "unknown";
};
export const validQuoteJourneySession = (value: unknown): value is string =>
  typeof value === "string" && /^[A-Za-z0-9_.:-]{1,128}$/.test(value);
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const attachmentKeys = [
  "attachment_id",
  "filename",
  "mime_type",
  "size",
  "document_id",
  "derived_format",
  "content_sha256",
  "derived_pdf_sha256",
  "quality_status",
  "quality_warning",
  "unknown_glyph_count",
  "affected_text_entities",
  "converter_image",
];
/** Project the upload DTO, which can contain server-only compatibility fields.
 * The persisted validator remains closed; storage keys never become Journey input. */
export function agentJourneyAttachments(
  values: readonly ChatAttachment[],
): ChatAttachment[] {
  return values.map(
    (value) =>
      Object.fromEntries(
        attachmentKeys
          .filter((key) => Object.prototype.hasOwnProperty.call(value, key))
          .map((key) => [
            key,
            (value as unknown as Record<string, unknown>)[key],
          ]),
      ) as unknown as ChatAttachment,
  );
}

function attachment(value: unknown): value is ChatAttachment {
  if (
    !object(value) ||
    Object.keys(value).some((key) => !attachmentKeys.includes(key)) ||
    typeof value.attachment_id !== "string" ||
    !/^[a-zA-Z0-9_.:-]{1,200}$/.test(value.attachment_id) ||
    typeof value.filename !== "string" ||
    value.filename.length > 500 ||
    typeof value.mime_type !== "string" ||
    value.mime_type.length > 200 ||
    typeof value.size !== "number" ||
    !Number.isSafeInteger(value.size) ||
    value.size < 0
  )
    return false;
  if (value.document_id != null && !quoteUuid(value.document_id)) return false;
  if (
    value.quality_status != null &&
    !["normal", "degraded"].includes(String(value.quality_status))
  )
    return false;
  if (value.derived_format != null && value.derived_format !== "pdf")
    return false;
  return (
    [
      "content_sha256",
      "derived_pdf_sha256",
      "quality_warning",
      "converter_image",
    ].every(
      (key) =>
        value[key] == null ||
        (typeof value[key] === "string" && value[key].length <= 4000),
    ) &&
    ["unknown_glyph_count", "affected_text_entities"].every(
      (key) =>
        value[key] == null ||
        (typeof value[key] === "number" &&
          Number.isSafeInteger(value[key]) &&
          value[key] >= 0),
    )
  );
}
export function readAgentJourney(
  page: JourneyPage | null,
  expectedSession?: string | null,
): AgentJourneyView | null {
  if (page?.key !== "agent" || page.version !== 1) return null;
  const v = page.data,
    keys = [
      "sessionId",
      "raw",
      "attachments",
      "context",
      "originalQuery",
      "initialPromptConsumed",
      "initialPromptState",
    ];
  if (
    keys.some((key) => !Object.prototype.hasOwnProperty.call(v, key)) ||
    Object.keys(v).some(
      (key) =>
        !keys.includes(key) &&
        !["reasoningPreference", "initialReasoningPreference"].includes(key),
    ) ||
    ["reasoningPreference", "initialReasoningPreference"].some((key) => {
      if (v[key] === undefined) return false;
      const preference = v[key];
      return (
        !object(preference) ||
        Object.keys(preference).length !== 3 ||
        !["model", "reasoningLevel", "capabilityRevision"].every(
          (field) =>
            preference[field] === null ||
            (typeof preference[field] === "string" &&
              preference[field].length <= 300),
        )
      );
    }) ||
    !validQuoteJourneySession(v.sessionId) ||
    (expectedSession != null && v.sessionId !== expectedSession) ||
    typeof v.raw !== "string" ||
    v.raw.length > 100000 ||
    typeof v.originalQuery !== "string" ||
    v.originalQuery.length > 4000 ||
    typeof v.initialPromptConsumed !== "boolean" ||
    !["pending", "submitted", "unknown"].includes(
      String(v.initialPromptState),
    ) ||
    (v.initialPromptState === "submitted" && !v.initialPromptConsumed) ||
    !Array.isArray(v.attachments) ||
    v.attachments.length > 100 ||
    !v.attachments.every(attachment)
  )
    return null;
  if (
    v.context !== null &&
    (!object(v.context) ||
      Object.keys(v.context).length !== 2 ||
      v.context.kind !== "factor_quote" ||
      !quoteUuid(v.context.id))
  )
    return null;
  if (
    page.files.some(
      (entry) =>
        !/^attachment-[0-9]{1,3}$/.test(entry.slot) ||
        Number(entry.slot.slice(11)) >= (v.attachments as unknown[]).length,
    )
  )
    return null;
  return v as unknown as AgentJourneyView;
}
